import { ProductComposedModelType } from '@teinor/erp/types/company/product/productComposed'
import { useCallback, useEffect, useState } from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { useParams } from 'react-router-dom'
import { ModalOpenedSetTy } from '../../../../../../baseComponents/Modal/types'
import { ModalCreateEditMedium } from '../../../../../../baseComponents/ModalsLayouts/ModalCreateEdit'
import { ModalEditComponentProps } from '../../../../../../baseComponents/ModalsLayouts/types'
import VirtualTableListing from '../../../../../../baseComponents/TwinTable/VirtualTableListing'
import { DeleteRow } from '../../../../../../baseComponents/TwinTable/VirtualTableListing/CustomRenders'
import { ColumnTableSchema } from '../../../../../../baseComponents/TwinTable/VirtualTableListing/Subcomponents/types'
import InputQuantity from '../../../../../../forms/Input/InputQuantity'
import SearchBarProduct from '../../../../SearchBarProduct'
import { OnChangeSearchBarAddProduct } from '../../../../SearchBarProduct/types'
import { OnSubmit, BeforeSubmitHandler } from '../../../../../../forms/TwinForm/types'
import { twinFetchPostJSON } from '../../../../../../utils/globals/data'
import { TwinDictionary } from '../../../../../../utils/globals/dictionary'
import withLoading from '../../../../../../utils/hoc/withLoading'
import useIsLoading from '../../../../../../utils/hooks/useIsLoading'
import useTwinTranslation from '../../../../../../utils/hooks/useTwinTranslation'
import { AllReduxPayloads } from '../../../../../../utils/reducers'
import { SpecificPriceRulesPayload } from '../../../../../../utils/reducers/products/specificPriceRules'
import { ProductExtendedTyLong } from '../../../../types'


interface ModalCEProductComposedProps extends ModalEditComponentProps { }

const ModalCEProductComposed: React.FC<ModalCEProductComposedProps & ReduxCreateProductComposed> = ({ userPermissions, allRowData, setOpened, ...rest }) => {
    const { t } = useTwinTranslation()
    const params = useParams<{ id: string }>()
    const id = params.id

    const { onSubmitHandler, beforeSubmit, changeData, data, deleteRow, changeUnits } = useCreateProductComposedLogic({ id, setOpened, ...rest })
    const columns: ColumnTableSchema<ProductExtendedTyLong & ProductComposedModelType> = [
        {
            id: 'id',
            dataKey: 'id',
            label: t('id', 'ID'),
            width: 50,
        },
        {
            id: 'name',
            dataKey: 'ProductLangs.name',
            label: t('name', 'Nombre'),
        },
        {
            id: 'reference',
            dataKey: 'reference',
            label: t('reference', 'Referencia'),
        },
        {
            id: 'units',
            dataKey: 'units',
            label: t('units', 'Unidades'),
            customRender: (parameterValue, allRowData) => 
                <InputQuantity onChange={(value) => changeUnits(value, allRowData.rowIndex)} typeQuantity={''} value={parameterValue as any}   />
        },
        {
            id: 'delete',
            dataKey: 'id',
            label: '',
            width: 50,
            simpleLabel: t('delete', 'Eliminar'),
            customRender: (parameterValue, allRowData) => <DeleteRow key={'delete' + allRowData.rowIndex} {...{ parameterValue, allRowData }} onClick={(_idk, allRowData) => deleteRow(allRowData.rowIndex)} />,
        }
    ]

    return (
        <ModalCreateEditMedium setOpened={setOpened} url='/api/app/product/productComposed/createProductComposed' onSubmit={onSubmitHandler} translations={{ title: t('addProductComposed', 'Añadir Producto Compuesto'), button: t('add', 'Añadir') }} haveButtonPermissions={true} fields={[]} beforeSubmit={beforeSubmit}>
            <SearchBarProduct changeSelectedItems={changeData} excludeIds={data.ids} id={id || ''} fieldNotIn='Product.id' name='ProductsComposedId' url='/api/app/product/getAllProducts' where={{ 'productType': { '$not': [5] } }} />
            <div className='flex-auto table_container mt-20'>
                <VirtualTableListing tableData={data.data} columns={columns} name='modalCreateProductComposed' />
            </div>
            <input type='hidden' value={id} name='ProductId' />
        </ModalCreateEditMedium>
    )
}

interface CreateProductComposedLogicProps {
    onSubmit: OnSubmit
    setOpened: ModalOpenedSetTy
    id?: string
}

const useCreateProductComposedLogic = ({ onSubmit, setOpened, id }: CreateProductComposedLogicProps) => {
    const [data, setData] = useState<{
        ids: string[] | number[]
        data: ProductExtendedTyLong[]
    }>({
        ids: [],
        data: []
    })

    const { endLoading, loading, startLoading } = useIsLoading()

    const onSubmitHandler: OnSubmit = useCallback((res, values) => {
        endLoading()
        onSubmit(res, values)
        setOpened(null)
    }, [endLoading, onSubmit, setOpened])

    const changeData: OnChangeSearchBarAddProduct = useCallback((item) => {
        setData((oldData) => {
            let hasId = false
            const copy = JSON.parse(JSON.stringify(oldData))
            for (const element of copy.data) {
                if (element.id === item.id) {
                    hasId = true
                }
            }
            if (hasId) {
                return copy
            }
            copy.data.push({ units: 1, ...item })
            copy.ids.push(item.id)
            return copy
        })
    }, [setData])

    const getData = useCallback(async () => {
        const ids = await twinFetchPostJSON('/api/app/product/productComposed/getAllProductsComposed', {
            where: { 'ProductId': id }, attributes: ['ProductsComposedId']
        })
        const parsed: number[] = []
        for (const key in ids) {
            const id = ids[key]['ProductsComposedId']
            parsed.push(id)
        }
        setData((old) => {
            return { ...old, ids: parsed }
        })
    }, [setData, id])
    const changeUnits = useCallback((value: string, rowIndex: number) => {
        setData((oldData) => {
            const copyOld = JSON.parse(JSON.stringify(oldData))
            if (!copyOld) {
                return copyOld
            }
            if (!copyOld.data[rowIndex].name) {
                copyOld.data[rowIndex].name = {}
            }
            if (value === undefined) {
                copyOld.data[rowIndex].units = 0
            } else {
                copyOld.data[rowIndex].units = value
            }
            return ({ ...copyOld })
        })
    }, [setData])

    const deleteRow = useCallback((rowIndex: number) => {
        setData((oldData) => {
            const copy = JSON.parse(JSON.stringify(oldData))
            const deleted = copy.data.splice(rowIndex, 1)
            const ids = []
            if (deleted.length) {
                for (const element of copy.ids) {
                    if (element !== deleted[0].id) {
                        ids.push(element)
                    }
                }
            }
            return ({ ...copy, ids })
        })
    }, [setData])

    const beforeSubmit: BeforeSubmitHandler = useCallback((vals) => {
        if (data) {
            let parseProductComposed: TwinDictionary = {}
            for (const key in data.data) {
                parseProductComposed[data.data[key].id] = { units: data.data[key].units || 1 }
            }
            vals.ProductsComposedId = parseProductComposed as any
        }
        startLoading()
        return true
    }, [startLoading, data])

    useEffect(() => {
        getData()
    }, [getData])

    return { onSubmitHandler, endLoading, loading, beforeSubmit, data, deleteRow, changeData, changeUnits }
}
const createProductComposedDispatch = {
    setSpecificPriceRules: (payload: SpecificPriceRulesPayload) => ({ type: 'CHANGE_SPECIFICPRICERULES', payload }),
}

const mapCreateProductComposedConnector = (state: AllReduxPayloads) => ({ specificPriceRules: state.specificPriceRules })
const createProductComposedConnect = connect(mapCreateProductComposedConnector, createProductComposedDispatch)
export type ReduxCreateProductComposed = ConnectedProps<typeof createProductComposedConnect>

const createProductComposedConnectLoading = withLoading(ModalCEProductComposed, [{ fetchUrl: '/api/app/product/specificPrice/getAllSpecificPriceRules', propName: 'specificPriceRules', setFunctionName: 'setSpecificPriceRules' }])
const ModalCEProductComposedConnect = createProductComposedConnect(createProductComposedConnectLoading)

export default ModalCEProductComposedConnect